/**
 * Visitor ID Manager
 * Handles race conditions when visitor ID needs to be set from booking
 */

import { v4 as uuidv4 } from 'uuid'
import { isMarketingPage } from './booking/common'

export const VISITOR_ID = 'visitor_id'

let visitorIdPromise = null

export const visitorIdManager = {
  /**
   * Get visitor ID with proper handling for different page types
   * @returns {string} The visitor ID
   */
  getVisitorId() {
    const currentPath = window.location.pathname
    const bookingEditPattern = /^\/bookings\/\d+\/edit$/

    if (!bookingEditPattern.test(currentPath) && !isMarketingPage) {
      let visitorId = sessionStorage.getItem(VISITOR_ID)
      if (!visitorId) {
        visitorId = uuidv4()
        sessionStorage.setItem(VISITOR_ID, visitorId)
      }
      return visitorId
    }

    return sessionStorage.getItem(VISITOR_ID) || ''
  },

  /**
   * Initialize a promise that will resolve when visitor ID is set for edit booking
   */
  initializeForEditBooking() {
    // Reset any existing promise
    if (visitorIdPromise) {
      visitorIdPromise = null
    }

    // Create a new promise that will be resolved when visitor ID is set
    visitorIdPromise = new Promise((resolve) => {
      this._resolveVisitorId = resolve

      // Add safety timeout to prevent infinite waiting
      setTimeout(() => {
        if (this._resolveVisitorId) {
          console.warn('Visitor ID timeout reached, falling back to session storage')
          const fallbackVisitorId = sessionStorage.getItem(VISITOR_ID)
          this._resolveVisitorId(fallbackVisitorId)
          this._resolveVisitorId = null
        }
      }, 3000)
    })

    return visitorIdPromise
  },

  /**
   * Set the visitor ID and resolve any waiting promises
   * @param {string} visitorId - The visitor ID from booking detail API
   */
  setVisitorId(visitorId) {
    // Resolve the promise if it exists
    if (this._resolveVisitorId) {
      this._resolveVisitorId(visitorId)
      this._resolveVisitorId = null
    }
  },

  /**
   * Clear the current visitor ID state (useful for navigation away from edit booking)
   */
  clear() {
    visitorIdPromise = null
    this._resolveVisitorId = null
  },

  /**
   * Common utility for removing visitor_id from sessionStorage and navigating to a URL
   * @param {string} url - The URL to navigate to
   */
  removeVisitorIdAndNavigate: (url) => {
    sessionStorage.removeItem(VISITOR_ID)
    window.location.href = url
  },

  /**
   * Set the visitor ID from booking detail response
   * @param {string} The visitor ID if it exists, an empty string otherwise
   */
  setVisitorIdFromDetail: (visitorId) => {
    const currentPath = window.location.pathname
    const bookingDetailPattern = /^\/bookings\/\d+$/
    const duplicatePattern = /^\/bookings\/\d+\/book_again/

    if (!visitorId) {
      return
    }

    // Skip setting visitor ID when on booking detail pages, duplicate booking pages
    if (bookingDetailPattern.test(currentPath) || duplicatePattern.test(currentPath)) {
      return
    }
    sessionStorage.setItem(VISITOR_ID, visitorId)
  }
}

export default visitorIdManager
